import { eventCategory } from "@foretagsplatsen/yaem";
import ErrorAlert from "./ErrorAlert.js";
import WarningAlert from "./WarningAlert.js";

/**
 * Aggregates several alerts.
 */
export default class AlertCollector {
	constructor() {
		this._events = eventCategory();
		this.changed = this._events.createEvent();

		this._alerts = [];
	}

	updateValidationResults({ model, context }) {
		this._alerts = [];
		model.validate({ collector: this, context });
		this.changed.trigger();
		return this;
	}

	reset() {
		this._alerts = [];
	}

	hasError() {
		return this._alerts.some((alert) => alert.isError());
	}

	getErrors() {
		return this._alerts.filter((alert) => alert.isError());
	}

	getResults({ includeWarnings = true } = {}) {
		return includeWarnings ? this._alerts : this.getErrors();
	}

	hasWarning() {
		return this._alerts.some((alert) => alert.isWarning());
	}

	getWarnings() {
		return this._alerts.filter((alert) => alert.isWarning());
	}

	isEmpty() {
		return this._alerts.length === 0;
	}

	assertEmpty() {
		if (this.isEmpty()) {
			return;
		}
		throw this._alerts[0].asException();
	}

	isSingleton() {
		return this._alerts.length === 1;
	}

	getFirstDescription() {
		return this._alerts[0].getDescription();
	}

	add(alert) {
		this._alerts.push(alert);
	}

	addError(description) {
		this.add(new ErrorAlert({ description }));
	}

	addErrors(descriptions) {
		descriptions.forEach((description) => this.addError(description));
	}

	addErrorIf(condition, description) {
		if (!condition) {
			return;
		}
		this.addError(description);
	}

	addWarning(description) {
		this.add(new WarningAlert({ description }));
	}

	addWarnings(descriptions) {
		descriptions.forEach((description) => this.addWarning(description));
	}

	addWarningIf(condition, description) {
		if (!condition) {
			return;
		}
		this.addWarning(description);
	}
}
